import * as React from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import { ConnectProto } from "@racoon/api";
import { FieldRow, Input, Label } from "@racoon/ui";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@racoon/ui";
import { PickupLocationForm } from "../ui/pickup-location.modal";
import { Namespace, useT } from "@racoon/i18n";
import { CoordChip } from "~/shared/ui/coord-chip";
import { TimeMask } from "~/shared/ui/time-mask";
import { TEXT_FIELD_MAX_LENGTH, validateRequiredText } from "@racoon/lib";
import { useListPickupLocationTypesQuery } from "~/shared/api/organization-labels/organization-labels.grpc";
import { assignableLabels } from "~/shared/lib/organization-labels";
import { withItem } from "~/shared/lib/own-organization";
import { LabelSelect } from "~/shared/ui/label.select";

export const InformationBlock: React.FC<{
  form: UseFormReturn<PickupLocationForm>;
  /** Whose pickup location types are offered: the location's service provider. */
  serviceProviderId?: string | number | null;
  /** The site's stored type, offered too: another provider's is on no list of ours. */
  storedType?: ConnectProto.OrganizationLabel;
}> = ({ form, serviceProviderId, storedType }) => {
  const t = useT();
  const {
    register,
    watch,
    control,
    setValue,
    formState: { errors },
  } = form;

  const twoGisUrl = watch("twoGisUrl");
  const is24h = watch("is24h");
  const typeId = watch("pickupLocationTypeId");

  const { currentData: typesResponse } = useListPickupLocationTypesQuery(
    { ownerOrganizationIds: serviceProviderId ? [BigInt(serviceProviderId)] : [] },
    { skip: !serviceProviderId },
  );
  const typeOptions = React.useMemo(
    () =>
      withItem(
        assignableLabels(typesResponse?.labels, typeId),
        String(storedType?.id) === typeId ? storedType : undefined,
      ),
    [typesResponse, typeId, storedType],
  );
  // A type found on no list (another provider's, not sent with the site) would show blank
  const isTypeUnknown =
    !!typesResponse && typeId !== "0" && !typeOptions.some((type) => String(type.id) === typeId);

  return (
    <div className="space-y-4">
      <FieldRow
        label={
          <>
            {t(Namespace.PickupLocation, "name")} <span className="text-red-500">*</span>
          </>
        }
      >
        <Input
          id="pickup-location-name-input"
          placeholder={t(Namespace.PickupLocation, "name")}
          aria-invalid={!!errors.name}
          maxLength={TEXT_FIELD_MAX_LENGTH}
          {...register("name", {
            validate: (value) => {
              const issue = validateRequiredText(value ?? "");
              if (issue) return t(Namespace.Validation, issue.key, issue.vars);
              return (value ?? "").trim().length > 0 || t(Namespace.Validation, "required");
            },
          })}
        />
        {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>}
      </FieldRow>

      {!isTypeUnknown && (
        <FieldRow label={t(Namespace.PickupLocation, "type")}>
          <Controller
            control={control}
            name="pickupLocationTypeId"
            render={({ field }) => (
              <LabelSelect
                id="pickup-location-type-select"
                options={typeOptions}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </FieldRow>
      )}

      <FieldRow label={t(Namespace.PickupLocation, "address")}>
        <Input
          id="pickup-location-address-input"
          placeholder={t(Namespace.PickupLocation, "addressPlaceholder")}
          {...register("address")}
        />
      </FieldRow>

      <FieldRow label={t(Namespace.PickupLocation, "placeLink", { link: "2GIS" })} alignTop>
        <div className="space-y-2">
          <Input
            id="pickup-location-twogis-input"
            placeholder="https://2gis.kz/almaty/geo/..."
            {...register("twoGisUrl")}
          />
          {!twoGisUrl && (
            <p className="text-xs text-muted-foreground">
              {t(Namespace.PickupLocation, "placeLinkDescription", { link: "2GIS" })}
            </p>
          )}
          {/* A point also comes from the pin placed on the map, with no link. */}
          {(twoGisUrl || watch("latitude")) && (
            <div className="flex gap-2">
              <CoordChip
                label={t(Namespace.PickupLocation, "latitude")}
                value={watch("latitude")}
              />
              <CoordChip
                label={t(Namespace.PickupLocation, "longitude")}
                value={watch("longitude")}
              />
            </div>
          )}
        </div>
      </FieldRow>

      <FieldRow label={t(Namespace.PickupLocation, "workTime")}>
        <div className="flex items-center gap-3">
          <Label className="inline-flex shrink-0 items-center gap-2">
            <input
              id="pickup-location-24h-checkbox"
              type="checkbox"
              checked={is24h}
              onChange={() => setValue("is24h", !is24h, { shouldDirty: true })}
            />
            {t(Namespace.PickupLocation, "24h")}
          </Label>

          <Controller
            name="workFrom"
            control={control}
            render={({ field }) => (
              <TimeMask disabled={is24h} contentEditable={!is24h} {...field}>
                {(inputProps: any) => (
                  <Input id="pickup-location-work-from-input" className="w-20" {...inputProps} />
                )}
              </TimeMask>
            )}
          />
          <Controller
            name="workTo"
            control={control}
            render={({ field }) => (
              <TimeMask disabled={is24h} contentEditable={!is24h} {...field}>
                {(inputProps: any) => (
                  <Input id="pickup-location-work-to-input" className="w-20" {...inputProps} />
                )}
              </TimeMask>
            )}
          />
        </div>
      </FieldRow>

      <FieldRow label={t(Namespace.PickupLocation, "priority")}>
        <Controller
          control={control}
          name="priority"
          render={({ field }) => (
            <Select value={String(field.value ?? "")} onValueChange={field.onChange}>
              <SelectTrigger id="pickup-location-priority-select" className="w-full">
                <SelectValue placeholder={t(Namespace.PickupLocation, "priority")} />
              </SelectTrigger>
              <SelectContent className="w-full">
                <SelectItem value="low">Низкий</SelectItem>
                <SelectItem value="medium">Средний</SelectItem>
                <SelectItem value="high">Высокий</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </FieldRow>

      {/* Участок — proto `geo_group`. Present in the form model but never rendered
          until now, so the value was invisible in the UI. */}
      <FieldRow label={t(Namespace.PickupLocation, "geozone")}>
        <Input
          id="pickup-location-geozone-input"
          placeholder={t(Namespace.PickupLocation, "geozone")}
          maxLength={TEXT_FIELD_MAX_LENGTH}
          aria-invalid={!!errors.geozone}
          {...register("geozone", {
            validate: (value) => {
              const issue = validateRequiredText(String(value ?? ""));
              return issue ? t(Namespace.Validation, issue.key, issue.vars) : true;
            },
          })}
        />
        {errors.geozone && (
          <p className="mt-1 text-sm text-red-500">{errors.geozone.message as string}</p>
        )}
      </FieldRow>

      {/* Read-only: derived from the location's container groups. */}
      <FieldRow label={t(Namespace.PickupLocation, "bunkerCount")}>
        <Input
          id="pickup-location-bunkers-input"
          type="number"
          disabled
          min={0}
          step={1}
          className="w-full"
          {...register("bunkers", { valueAsNumber: true })}
        />
      </FieldRow>
    </div>
  );
};
